import * as React from 'react';
import pupa from 'pupa';
import { TextField, CircularProgress, makeStyles, Theme, createStyles, Typography, Popper, Chip } from '@material-ui/core';
import { ResourceRelationsService } from '../services/ResourceRelationsService';
import strings from './../utils/StringManager';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ResourcesAutocompleteSearch from './ResourcesAutocompleteSearch';
import { IGridResourcesConfig } from './Relations/GridResourcesConfig';
import clsx from 'clsx';
import { IResourceData, useGetResourceGridApi } from '../services/useResourcesForGridApi';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			display: 'flex',
			alignItems: 'center',
		},
		combobox: {
			flex: '1',
		},
		search: {
			marginLeft: '7px',
		},
		option: {
		},
		tag: {
			maxWidth: "200px",
		}
	}),
);

export interface OptionType {
	value: string,
	label: string,
	description?: string,
	kind: string,
}

export interface IAutocompleteParams {
	maxRows: number,
	filterId?: string,
}

function dataMapper(item: { id: number, value: string, label: string }, kind: string): OptionType {

	return {
		value: item.id.toString(),
		label: item.label,
		kind: kind,
		description: "",
	};
}

interface IProps {
	value: OptionType[],
	onSelect: (value: OptionType[]) => void,
	filters: IAutocompleteParams,
	kind?: 'Application' | 'ServiceAsset' | 'BusinessProcess' | 'DataSet' | 'Employee' | 'PersonProfile',
	multiple?: true | false,
	placeholderText: string,

	grid: IGridResourcesConfig,
}

export default function ResourcesAutocompleteMI(props: IProps)
{
	const [loading, setLoading] = React.useState(false);
	const [options, setOptions] = React.useState<OptionType[]>([]);
	const [inputValue, setInputValue] = React.useState('');
	const classes = useStyles();

	const [setRequest, resourceGridData, isLoading, isError] = useGetResourceGridApi();

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(event.target.value);
	};


	React.useEffect(() => {
		let active = true;

		if (inputValue === '') {
			setOptions([]);
			setLoading(false);
			return;
		}

		setLoading(true);

		//filterId nie jest wspierany przez mechanizm grid'u
		if (!props.filters?.filterId && props.grid) {
			let template = props.grid.labelTemplate;
			let searchFields = [];
			if (template) {
				searchFields = props.grid.columns
					.filter(x =>
						x.searched && template.indexOf('{' + x.id + '}') > -1
					)
					.map(x => x.id);
			} else {
				searchFields = ['name'];
			}
			setRequest({
				dir: 'Asc',
				filters: props.grid?.filters ?? "",
				kind: props.kind,
				orderBy: 'name',
				search: inputValue,
				skip: 0,
				take: props.filters.maxRows,
				searchFields: searchFields
			})
		}
		else {
			let service = new ResourceRelationsService();
			service.getResources(props.kind, props.filters, inputValue, (data) => {
				if (inputValue === data.searchText) {
					const items: OptionType[] = data.options.map(x => dataMapper(x, props.kind));

					setOptions(items);
					setLoading(false);
				}
			});
		}

		return () => {
			active = false;
		};
	}, [inputValue, fetch]);

	React.useEffect(() => {
		if (!resourceGridData)
			return;

		if (resourceGridData.searchText === inputValue) {
			let items = parseGridOptionsForAutocomplete(resourceGridData.rows, props.kind, props.grid);
			setLoading(false);
			setOptions(items);
		}

	}, [resourceGridData]);

	function singleValue(v: OptionType[]): OptionType {
		if (!v || v.length == 0)
			return null;
		return v[0];
	}

	let noOptionsText = strings.get("enterTextToSearch") + "...";
	if (inputValue) {
		noOptionsText = strings.format("noOptionsFoundForPhrase", inputValue);
	}

	let placeholder = strings.get("search") + "...";
	if (props.placeholderText) {
		placeholder = props.placeholderText;
	};
	let autocomplete: React.ReactElement = null;

	const renderOption = (option: OptionType) => (<div>
		<Typography variant="body1" color="textPrimary" className={classes.option}>{option.label}</Typography>
		<Typography variant="body2" color="textSecondary">
			{option.description}
		</Typography>
	</div>);

	if (!props.multiple) {
		autocomplete = (
			<Autocomplete
				className={clsx(classes.combobox, "mi-lab-autocomplete-outlined")}
				value={singleValue(props.value)}
				onChange={(event, newValue) => {
					if (!newValue)
						props.onSelect([]);
					else {
						let v = newValue as OptionType;
						props.onSelect([v]);
					}
				}}
				getOptionLabel={(option: OptionType) => option?.label ?? ""}
				getOptionSelected={(option: OptionType, value: OptionType) => option.value == value.value}
			
				options={options}
				loading={loading}
				filterOptions={x => x}
				autoComplete
				includeInputInList
				noOptionsText={noOptionsText}
				renderInput={params => (
					<TextField
						{...params}
						variant="outlined"
						fullWidth
						onChange={handleChange}
						InputProps={{
							...params.InputProps,
							autoComplete: 'disabled',
							placeholder: placeholder,
							endAdornment: (
								<React.Fragment>
									{loading ? <CircularProgress color="inherit" size={20} /> : null}
									{params.InputProps.endAdornment}
								</React.Fragment>
							),
						}}
					/>
				)}

				PopperComponent={PopperMy}
				renderOption={renderOption}
			/>)
	}
	else {
		autocomplete = <Autocomplete
			multiple
			className={clsx(classes.combobox, "mi-lab-autocomplete-outlined")}
			value={props.value}
			onChange={(event, newValue) => {
				let v = newValue as OptionType[];
				props.onSelect(v);
			}}
			getOptionLabel={(option: OptionType) => option?.label ?? ""}
			getOptionSelected={(option: OptionType, value: OptionType) => option.value == value.value}
			options={options}
			loading={loading}
			filterOptions={x => x}
			autoComplete
			includeInputInList
			noOptionsText={noOptionsText}

			renderInput={params => (
				<TextField
					{...params}
					variant="outlined"
					fullWidth
					onChange={handleChange}
					InputProps={{
						...params.InputProps,
						autoComplete: 'disabled',
						placeholder: placeholder,
						endAdornment: (
							<React.Fragment>
								{loading ? <CircularProgress color="inherit" size={20} /> : null}
								{params.InputProps.endAdornment}
							</React.Fragment>
						),
					}}
				/>
			)}

			PopperComponent={PopperMy}
			renderOption={renderOption}
			renderTags={(value, getTagProps) =>
				value.map((option, index) => (
					<Chip
						{...getTagProps({ index })}
						label={option.label}
						className={clsx(classes.tag, "resource-autocomplete-tag")}
						title={option.label}
					/>
				))
			}
		/>
	}

	return <div className={"mi-lab-combobox " + classes.root} >
		{autocomplete}
		{props.grid && !props.grid.hidden && <ResourcesAutocompleteSearch
			className={classes.search}

			kind={props.kind}
			onSelect={props.onSelect}
			value={props.value}
			multiple={props.multiple}

			columns={props.grid.columns}
			filters={props.grid.filters}
			title={props.grid.title}
			rowsPerPageOptions={props.grid.rowsPerPageOptions}
		/>}
	</div>
}

function fillTemplateStrings(x: IResourceData, gridConfig: IGridResourcesConfig) {
	let label = x.label ?? x.name;
	let description = "";

	if (gridConfig?.labelTemplate || gridConfig?.descriptionTemplate) {
		for (let p in x) {
			if (x[p] === null || x[p] === undefined || x[p] === "") {
				x[p] = "-";
			}
		}
	}

	if (gridConfig?.labelTemplate) {
		label = pupa(gridConfig.labelTemplate, x);
	}

	if (gridConfig?.descriptionTemplate) {
		description = pupa(gridConfig.descriptionTemplate, x);
	}

	return { label, description };
}

export function parseGridOptionsForAutocomplete(rows: IResourceData[], kind: string, gridConfig: IGridResourcesConfig) {
	
	let items: OptionType[] = [];
	if (rows) {

		items = rows.map(x => {

			const { label, description } = fillTemplateStrings(x, gridConfig);

			let truncatedLabel = label;
			if (label.length > 250) {
				truncatedLabel = label.substring(0, 250) + "...";
			}

			let truncatedDescription = description;
			if (description.length > 250) {
				truncatedDescription = description.substring(0, 250) + "...";
			}
			
			return {
				value: x.id.toString(),
				label: truncatedLabel,
				fullLabel: label,
				kind: kind,
				description: truncatedDescription,
			}
		});
	}

	return items;
}

const PopperMy = function (props) {
	return (<Popper {...props} style={{ width: 'auto' }} placement='bottom-start' />)
}